import React, { useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  flip,
  shift,
  offset,
  autoUpdate,
  type Placement,
} from '@floating-ui/react'
import { TaskDropdownProps } from './TaskDropdown.types'

const TITLEBAR_HEIGHT = 48
const VIEWPORT_PADDING = 8

const COLLISION_PADDING = {
  top: TITLEBAR_HEIGHT + VIEWPORT_PADDING,
  bottom: VIEWPORT_PADDING,
  left: VIEWPORT_PADDING,
  right: VIEWPORT_PADDING,
}

export const TaskDropdown: React.FC<TaskDropdownProps> = ({
  open,
  onOpenChange,
  trigger,
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  contentStyle,
  reserveBottom = 0,
  disableResize = false,
  fallbackPlacements = ['right-start', 'left-start'] as Placement[],
  alignToContainerRight,
  instant = false,
}) => {
  const placement = `${side}-${align}` as Placement

  const middleware = useMemo(() => {
    const chain = [
      offset({ mainAxis: sideOffset, crossAxis: alignOffset }),
      flip({
        padding: { ...COLLISION_PADDING, bottom: COLLISION_PADDING.bottom + reserveBottom },
        fallbackPlacements,
        fallbackStrategy: 'initialPlacement',
      }),
      shift({ mainAxis: true, crossAxis: true, padding: { ...COLLISION_PADDING, bottom: COLLISION_PADDING.bottom + reserveBottom } }),
    ]
    // Custom middleware: override horizontal position to align dropdown's right edge
    // with the nearest .task-entry container, inset by alignToContainerRight pixels.
    if (alignToContainerRight != null) {
      chain.push({
        name: 'alignToContainer',
        fn({ elements, rects, placement: finalPlacement }) {
          // Only align to container when dropdown opens below/above the trigger.
          // When flipped to left/right (not enough vertical space), stay at the trigger.
          if (finalPlacement.startsWith('left') || finalPlacement.startsWith('right')) return {}
          const trigger = elements.reference as HTMLElement
          const entry = trigger.closest('.task-entry') as HTMLElement | null
          if (!entry) return {}
          const entryRect = entry.getBoundingClientRect()
          const newX = entryRect.right - alignToContainerRight - rects.floating.width
          return { x: newX }
        },
      })
    }
    return chain
  }, [sideOffset, alignOffset, reserveBottom, disableResize, fallbackPlacements, alignToContainerRight])

  const { refs, floatingStyles, context, isPositioned, placement: finalPlacement } = useFloating({
    open,
    onOpenChange,
    placement,
    // Disable elementResize so the dropdown doesn't reposition when dynamic
    // content (e.g. confirm footer) appears — it grows downward instead.
    whileElementsMounted: (reserveBottom > 0 || disableResize)
      ? (reference, floating, update) => autoUpdate(reference, floating, update, { elementResize: false })
      : autoUpdate,
    middleware,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

   
  const triggerWithRef = React.cloneElement(trigger as React.ReactElement<any>, {
    ref: refs.setReference,
    ...getReferenceProps(),
  })

  return (
    <>
      {triggerWithRef}
      {open && createPortal(
        <div
          data-task-dropdown-backdrop
          style={{ position: 'fixed', inset: 0, zIndex: 49 }}
          onClick={(e) => { e.stopPropagation(); onOpenChange(false) }}
          onPointerDown={(e) => e.stopPropagation()}
        />,
        document.querySelector('.main-layout__body') ?? document.body
      )}
      {open && createPortal(
        <div
          ref={refs.setFloating}
          data-task-dropdown
          data-placement={finalPlacement}
          className="context-menu-content"
          style={{
            ...floatingStyles,
            ...contentStyle,
            zIndex: 50,
            animation: isPositioned && !instant ? 'task-dropdown-fade-in 0.15s ease-out' : 'none',
            visibility: isPositioned ? 'visible' : 'hidden',
          }}
          {...getFloatingProps()}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  )
}
